import React from "react"
import clsx from "clsx"
import { useLocation } from "react-router-dom"
import { gt } from "@/shared/config/i18n"
import { getLastPath } from "@/shared/utils"
import "./style.scss"
import type { JSX } from "react/jsx-runtime"
import SimpleBar from "simplebar-react"
import "simplebar-react/dist/simplebar.min.css"
import type { IBaseRole } from "@/entities/auth/types.ts"
import type { IPermissionBase } from "@/entities/permission/types.ts"
import type { IResourceKeyUnion } from "@/shared/types/i18n-resource-keys.ts"

export interface TabItem<M extends object = object> {
  label: IResourceKeyUnion | string
  icon?: string
  element?: JSX.Element
  allowedPermissions?: IPermissionBase[]
  disallowedRoles?: IBaseRole[]
  id?: number
  path?: string
  meta?: M
}

interface ITabBarProps<T extends object = object> {
  tabs: TabItem<T>[]
  className?: string
  classNames?: {
    tab: string
  }
  exact?: boolean
  size?: "sm" | "md"
  spacing?: number
  value?: TabItem<T>
  eventName?: string
  onChange?: (tab: TabItem<T>) => void
  renderTab?: (tab: TabItem<T>) => JSX.Element
}

const TabComponent = <T extends object = object>({
  size = "md",
  spacing = 0,
  eventName,
  tabs,
  className,
  classNames,
  onChange,
  value,
  renderTab,
  exact
}: ITabBarProps<T>) => {
  const { pathname } = useLocation()

  if (exact && getLastPath(pathname) !== value?.path) return null

  return (
    <SimpleBar style={{ width: "100%" }}>
      <div className={clsx("tab", `tab-size--${size}`, className)} style={{ gap: spacing }}>
        {tabs.map(tab => (
          <div
            key={tab.label}
            role="button"
            tabIndex={0}
            className={clsx(
              "tab-item m-1 select-none rounded-lg ring-blue-400 transition focus:bg-blue-600/10 focus:outline-none",
              value?.label === tab.label ? "tab-item--active" : "",
              className
            )}
            onKeyDown={e => {
              if (e.key === "Enter" && value?.label !== tab.label) {
                onChange?.(tab)
              }
            }}
            onClick={() => onChange?.(tab)}
          >
            {renderTab != null ? renderTab(tab) : gt(tab.label as IResourceKeyUnion)}
          </div>
        ))}
      </div>
    </SimpleBar>
  )
}

export const Tab = React.memo(TabComponent) as typeof TabComponent
